import { createSignal, onCleanup, onMount } from "solid-js";

export interface AnchoredMenuRect {
  left: number;
  top: number;
  width: number;
}

// Menus render through a Portal with position: fixed, because the pickers sit inside modals and
// scrolling panels whose overflow would otherwise clip them.
export function useAnchoredMenu(maxHeight: number) {
  const [open, setOpen] = createSignal(false);
  const [menuRect, setMenuRect] = createSignal<AnchoredMenuRect>({ left: 0, top: 0, width: 0 });

  let containerRef: HTMLElement | undefined;
  let triggerRef: HTMLElement | undefined;
  let menuRef: HTMLElement | undefined;

  const reposition = () => {
    if (!triggerRef) return;
    const triggerRect = triggerRef.getBoundingClientRect();
    const menuHeight = menuRef?.offsetHeight ?? maxHeight;
    const spaceBelow = window.innerHeight - triggerRect.bottom;
    const spaceAbove = triggerRect.top;
    const openUpward = spaceBelow < menuHeight + 12 && spaceAbove > spaceBelow;
    const top = openUpward ? Math.max(8, triggerRect.top - menuHeight - 6) : triggerRect.bottom + 6;
    setMenuRect({ left: triggerRect.left, top, width: triggerRect.width });
  };

  const show = () => {
    reposition();
    // The first pass guesses the menu height; the second measures it once it has rendered.
    queueMicrotask(reposition);
    setOpen(true);
  };

  const toggle = () => (open() ? setOpen(false) : show());

  const handleClickOutside = (event: MouseEvent) => {
    const target = event.target as Node;
    if (containerRef?.contains(target) || menuRef?.contains(target)) return;
    setOpen(false);
  };
  const handleReposition = () => { if (open()) reposition(); };

  onMount(() => {
    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleReposition, true);
    window.addEventListener("resize", handleReposition);
    onCleanup(() => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleReposition, true);
      window.removeEventListener("resize", handleReposition);
    });
  });

  return {
    open,
    setOpen,
    toggle,
    reposition,
    menuRect,
    setContainerRef: (element: HTMLElement) => { containerRef = element; },
    setTriggerRef: (element: HTMLElement) => { triggerRef = element; },
    setMenuRef: (element: HTMLElement) => { menuRef = element; },
  };
}
